/**
 * One toast: a status line, not a banner.
 *
 * Surface ground + hairline + a 3px state stripe, so the colour says WHICH
 * state it is without the message itself being coloured — "semantic colour is a
 * state, not a decoration". The stripe uses the `-fill` tokens (they do not
 * flip, which is what a fill needs) and the icon uses the `-fg` text roles
 * (they do, and they carry the measured AA contrast on `surface`). Nothing here
 * puts white on a semantic fill.
 *
 * The toast brings its own `bg-overlay` ground rather than borrowing the one it
 * floats over, so there is no `*-on-chrome` variant: over the Studio's dark
 * stage it is still a card on top, not text on chrome.
 *
 * `bg-overlay` and not `bg-surface`: a toast has no scrim under it (a modal
 * does), so it has to out-contrast the live page on its own. In dark mode
 * `bg-surface` #18181B sits BELOW the shell ground #27272A and the 8%-black
 * `--shadow-elevated` is invisible there, so the thing read as an outlined hole
 * — the owner's 2026-09-16 note. The overlay role steps up in both themes and
 * `--shadow-overlay` flips with them.
 */
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ToastRecord, ToastVariant } from './toastStore';

const ICON: Record<ToastVariant, typeof Info> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

const STRIPE: Record<ToastVariant, string> = {
  success: 'bg-ok-fill',
  info: 'bg-notice-fill',
  warning: 'bg-warn-fill',
  error: 'bg-bad-fill',
};

const TONE: Record<ToastVariant, string> = {
  success: 'text-ok',
  info: 'text-notice',
  warning: 'text-warn',
  error: 'text-bad',
};

/** Long enough for the exit transition in index.css, short enough not to lag. */
const EXIT_MS = 160;

export function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  const { id, message, variant, duration, action } = toast;
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  // What is LEFT of the timer, so hovering pauses rather than restarts it.
  const remaining = useRef(duration);

  useEffect(() => {
    if (duration <= 0 || paused || leaving) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => setLeaving(true), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [duration, paused, leaving]);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => onDismiss(id), EXIT_MS);
    return () => clearTimeout(timer);
  }, [leaving, id, onDismiss]);

  const Icon = ICON[variant];
  const isError = variant === 'error';

  return (
    <li
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={clsx(
        'fx-toast pointer-events-auto relative flex items-start gap-2.5 overflow-hidden rounded-[10px]',
        'border border-line-strong bg-overlay py-2.5 pl-4 pr-2 shadow-[var(--shadow-overlay)]',
        leaving && 'fx-toast-leaving',
      )}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span aria-hidden className={clsx('absolute inset-y-0 left-0 w-[3px]', STRIPE[variant])} />
      <Icon aria-hidden className={clsx('mt-px h-4 w-4 shrink-0', TONE[variant])} strokeWidth={2} />
      <p className="min-w-0 flex-1 text-small font-medium leading-snug text-strong">{message}</p>
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); setLeaving(true); }}
          className="shrink-0 rounded px-1 text-small font-medium text-accent-ink hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss notification"
        data-testid="toast-dismiss"
        onClick={() => setLeaving(true)}
        className="shrink-0 rounded p-1 text-faint transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </li>
  );
}
